<template>
  <!-- MAC 수동 등록 — 전화기가 아직 설정 서버에 접속하지 않았어도 미리 넣어 둔다(복구 코드 흐름의 출발점) -->
  <a-modal :open="open" :footer="null" :mask-closable="false" destroy-on-close @cancel="close">
    <template #title>
      <div class="modal-title">전화기 등록</div>
    </template>

    <div class="modal-content pbx-dev-reg">
      <p v-if="formError" class="pbx-dev-reg__error" role="alert">{{ formError }}</p>

      <!-- a-select 는 label 로 감싸지 않는다(라벨 클릭이 드롭다운을 엉뚱하게 여닫는다) -->
      <div class="pbx-dev-reg__field">
        <span class="pbx-dev-reg__label">설치 장소</span>
        <a-select
          v-model:value="siteId"
          :options="siteOptions"
          placeholder="설치 장소 선택"
          not-found-content="설치 장소가 없습니다"
        />
        <span v-if="!siteOptions.length" class="pbx-dev-reg__hint">
          사용 중인 설치 장소가 없습니다. 설치 장소 탭에서 먼저 만드세요.
        </span>
      </div>

      <label class="pbx-dev-reg__field">
        <span class="pbx-dev-reg__label">MAC</span>
        <input
          v-model="macInput"
          class="pbx-dev-reg__input"
          :class="{ 'is-invalid': macInvalid }"
          placeholder="예: 00:04:f2:aa:00:03"
          autocomplete="off"
          spellcheck="false"
          @blur="macTouched = true"
          @keydown.enter="onEnter"
        />
        <span v-if="macInvalid" class="pbx-dev-reg__hint pbx-dev-reg__hint--error">
          MAC 은 16진수 12자리입니다(구분자 : - . 는 빼고 보냅니다).
        </span>
        <span v-else class="pbx-dev-reg__hint">전화기 뒷면 라벨의 MAC. 구분자는 넣어도 됩니다.</span>
      </label>

      <label class="pbx-dev-reg__field">
        <span class="pbx-dev-reg__label">라벨 <em>(선택)</em></span>
        <input
          v-model="label"
          class="pbx-dev-reg__input"
          placeholder="예: 원무과 1번 자리"
          maxlength="64"
          @keydown.enter="onEnter"
        />
      </label>

      <div class="modal-footer">
        <a-button @click="close">취소</a-button>
        <a-button type="primary" class="yellow-button" :loading="saving" :disabled="!canSubmit" @click="submit">
          등록
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script setup>
import { computed, ref, watch } from 'vue';

import { getCallApiErrorCode } from '@/views/Callbot/service/callApiError';
import { usePbxAction } from '@/views/Callbot/service/usePbxAction';
import { normalizeMac } from '@/views/Callbot/service/pbxExtensions';

import { isImeEnter } from './PbxDeviceShared';

const props = defineProps({
  open: { type: Boolean, default: false },
  inventory: { type: Object, required: true },
});
const emit = defineEmits(['update:open', 'created']);

const { run, isPending } = usePbxAction(props.inventory);

const siteId = ref(undefined);
const macInput = ref('');
const macTouched = ref(false);
const label = ref('');
const formError = ref('');

// 폐기된 설치 장소에는 새 전화기를 넣지 않는다
const siteOptions = computed(() =>
  props.inventory.sites.items.value
    .filter((s) => s.status === 'active')
    .map((s) => ({ value: s.id, label: s.name }))
);

const mac = computed(() => normalizeMac(macInput.value));
const macInvalid = computed(() => macTouched.value && !!macInput.value.trim() && !mac.value);
const saving = computed(() => isPending('device:create'));
const canSubmit = computed(() => !!siteId.value && !!mac.value && !saving.value);

// 열 때마다 초기화 — 설치 장소가 하나뿐이면 미리 고른다
watch(
  () => props.open,
  (open) => {
    if (!open) return;
    macInput.value = '';
    macTouched.value = false;
    label.value = '';
    formError.value = '';
    siteId.value = siteOptions.value.length === 1 ? siteOptions.value[0].value : undefined;
  }
);

function close() {
  emit('update:open', false);
}

function onEnter(e) {
  if (isImeEnter(e)) return;
  e.preventDefault();
  void submit();
}

async function submit() {
  macTouched.value = true;
  if (!canSubmit.value || !siteId.value || !mac.value) return;
  formError.value = '';
  const trimmed = label.value.trim();
  const res = await run(
    'device:create',
    () =>
      props.inventory.api.createPbxDevice({
        site_id: siteId.value,
        mac: mac.value,
        ...(trimmed ? { label: trimmed } : {}),
      }),
    {
      success: '전화기 등록을 완료했습니다.',
      reload: ['devices', 'sites'],
      fallback: '전화기를 등록하지 못했습니다.',
      // 모달에서 시작한 조작 — 실패는 모달 상단에 인라인으로, 모달은 닫지 않는다
      onError: (err, text) => {
        formError.value =
          getCallApiErrorCode(err) === 'PBX_DEVICE_MAC_ALREADY_REGISTERED' ? '이미 등록된 MAC 입니다.' : text;
        return true;
      },
    }
  );
  if (!res) return;
  emit('created', res);
  close();
}
</script>

<style scoped lang="scss">
.pbx-dev-reg {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-top: 4px;

  &__error {
    margin: 0;
    padding: 8px 12px;
    border: 1px solid #ffccc7;
    border-radius: 8px;
    background: #fff2f0;
    color: #cf1322;
    font-size: 13px;
    line-height: 1.5;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__label {
    font-size: 13px;
    font-weight: 600;
    color: #262626;

    em {
      font-style: normal;
      font-weight: 400;
      color: #8c8c8c;
    }
  }

  &__input {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    font-size: 13px;

    &:focus {
      outline: none;
      border-color: #1668dc;
    }

    &.is-invalid {
      border-color: #ff4d4f;
    }
  }

  &__hint {
    font-size: 12px;
    color: #8c8c8c;
    line-height: 1.5;

    &--error {
      color: #cf1322;
    }
  }

  // admin 모달 규약 — 전역 .modal-content 의 테두리·최소 높이는 antd 모달 안에서 쓰지 않는다.
  // 전역 .ant-modal-content { padding: 0 } 이라 좌우·하단 여백은 여기서 준다(PbxSiteFormModal 과 같은 값)
  &.modal-content {
    padding: 4px 24px 20px;
    border: none;
    border-radius: 0;
    min-height: 0;
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 8px 0 0;
    border-top: none;
  }
}

.modal-title {
  font-size: 16px;
  font-weight: bold;
}

/* 전역 정의가 없는 클래스라 쓰는 컴포넌트마다 들고 있어야 한다. */
:deep(.yellow-button) {
  background-color: #ffcc32 !important;
  border-color: #ffcc32 !important;
  color: #000 !important;

  &:hover {
    background-color: #e6b82e !important;
    border-color: #e6b82e !important;
  }

  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}
</style>
